import Share from '@/assets/icons/share.svg?react'
import { routes } from '@/pages'
import { Button, Text, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { formatBytes, formatNumber } from '@/utils'
import { plural } from '@lingui/core/macro'
import { Plural, Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Clock, CloudUpload, Star, Users, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mission, MISSION_AVAILABILITY_REASON_UNAVAILABLE, TYPE_TEXT_MAP } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import { getBadgeVariant, MissionBadge, STATUS_TEXT_MAP } from './MissionBadge'
import { MissionCardMainActionButton } from './MissionCardMainButton'
import { MissionRewardBadge } from './MissionRewardBadge'

type MissionCardCollapsedProps = {
  mission: Mission
  basePath?: string
  highlighted?: boolean
  isMinified?: boolean
}

export const MissionCard = ({ mission, highlighted = false, isMinified = false }: MissionCardCollapsedProps) => {
  const { t } = useLingui()
  const formatReward = (amount: number) =>
    mission.rewardType === 'usd' ? `$${formatNumber(amount, 2)}` : plural(amount, { one: '# point', other: '# points' })
  const reward = formatReward(mission.view.accruedReward)
  const extraReward = mission.type === 'WEBSITE_LOGIN' ? formatReward(mission.view.earnedPoints) : ''
  const upToReward = formatReward(mission.reward)
  const uploadedSize = mission.type === 'FILE_UPLOAD' ? formatBytes(mission.view.uploadedBytes) : ''
  const rewardUnit = mission.rewardType === 'usd' ? '$' : 'points'
  const joinedCount = mission.view.joined
  const executedJobsCount = mission.type === 'WEBSITE_LOGIN' ? mission.view.executedJobs : 0
  const multiplier = mission.type === 'WEBSITE_LOGIN' ? mission.view.multiplier : 0
  const { missionsUrl } = useMissionsUrl()
  const [copied, setCopied] = useState(false)

  const detailPath =
    mission.type === 'STAKING' ? routes.stake.path : (mission.detailPath ?? `${missionsUrl}/${mission.id}`)

  const shareURL =
    mission.type === 'STAKING'
      ? `${window.location.origin}${missionsUrl}?missionId=${mission.id}`
      : `${window.location.origin}${detailPath}`

  const handleShare = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    void navigator.clipboard.writeText(shareURL).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  const isVoiceMission = ['VOICE_CONVERSATION', 'AUDIO_RECORDING', 'AUDIO_TRANSCRIPTION'].includes(mission.type)
  const isUnavailable =
    mission.status === 'ENDED' ||
    (!mission.availability?.available && MISSION_AVAILABILITY_REASON_UNAVAILABLE.includes(mission.availability?.reason))
  return (
    <li
      id={`mission-card-${mission.id}`}
      className={clsx(
        'cursor-pointer rounded-2xl border border-solid p-4 transition-all',
        isMinified ? 'min-h-[180px]' : 'min-h-[300px]',
        {
          'hover:border-orange border-gray-100': !isUnavailable,
          'border-gray-100 hover:border-gray-200/50': isUnavailable,
          'border-green hover:border-green!':
            mission.view.latestSubmissionStatus === 'COMPLETED' && !mission.view.nextSubmissionAvailable,
          'ring-orange ring-offset-background z-1 shadow-[0_0_18px_6px_color-mix(in_srgb,var(--color-orange)_42%,transparent),0_0_28px_10px_color-mix(in_srgb,var(--color-orange)_22%,transparent)] ring-2 ring-offset-1':
            highlighted
        }
      )}
    >
      <Link className="flex h-full flex-col gap-4" to={detailPath || '#'}>
        <div className="flex justify-between gap-1">
          <div className="flex gap-1">
            {isUnavailable ? (
              <MissionBadge>
                <Trans>Unavailable</Trans>
              </MissionBadge>
            ) : (
              <>
                {mission.tags.length && (isVoiceMission || !mission.view.latestSubmissionStatus) ? (
                  <MissionBadge variant={'warning'}>{mission.tags[0]}</MissionBadge>
                ) : (
                  <MissionBadge variant={getBadgeVariant(mission)} tooltip={mission.view.tooltip}>
                    {mission.view.latestSubmissionStatus === 'SUBMITTED' ? (
                      <>
                        <Clock size={15} />
                        {t(STATUS_TEXT_MAP.SUBMITTED)}
                      </>
                    ) : mission.view.latestSubmissionStatus === 'COMPLETED' &&
                      mission.view.nextSubmissionAvailable &&
                      mission.type === 'WEBSITE_LOGIN' ? (
                      t`Attention`
                    ) : (
                      t(STATUS_TEXT_MAP[mission.view.latestSubmissionStatus ?? 'null'] ?? STATUS_TEXT_MAP.null)
                    )}
                  </MissionBadge>
                )}
              </>
            )}

            <MissionBadge variant="default">
              <div className="block max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap">
                {TYPE_TEXT_MAP[mission.type] ? t(TYPE_TEXT_MAP[mission.type]) : mission.type}
              </div>
            </MissionBadge>
          </div>
          <MissionRewardBadge amount={mission.reward} rewardType={mission.rewardType} className="max-mobile:hidden" />
        </div>
        <div className="flex grow flex-col justify-between gap-1">
          <div className="flex flex-col gap-2">
            <Text type="subtitle-semibold" className={clsx('text-left transition-colors')}>
              {mission.title}
            </Text>
            <Text type="body2-regular" className="text-gray-90 line-clamp-3">
              {mission.shortDescription}
            </Text>
          </div>
          {!isMinified && (
            <div className="py-6">
              {mission.view.accruedReward > 0 || (mission.type === 'WEBSITE_LOGIN' && mission.view.earnedPoints > 0) ? (
                <>
                  <div className="text-gray-70 flex items-start gap-2">
                    <Star size={16} className="text-green mt-1 shrink-0" />
                    <Trans>You got {reward}</Trans>
                  </div>
                  {mission.type === 'WEBSITE_LOGIN' && mission.view.earnedPoints > 0 && (
                    <div className="text-gray-70 flex items-start gap-2">
                      <Zap size={16} className="text-green mt-1 shrink-0" />
                      <Trans>You earned extra {extraReward}</Trans>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {mission.reward > 0 && (
                    <div className="text-gray-70 flex items-start gap-2">
                      <Star size={16} className="text-green mt-1 shrink-0" />
                      <Trans>Earn up to {upToReward} for completing the mission</Trans>
                    </div>
                  )}
                  {mission.type === 'WEBSITE_LOGIN' && mission.view.multiplier > 1 && (
                    <div className="text-gray-70 flex items-start gap-2">
                      <Zap size={16} className="text-green mt-1 shrink-0" />
                      <Trans>
                        Earn x{multiplier} {rewardUnit} on jobs
                      </Trans>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="flex flex-col gap-4">
            {!isMinified && (
              <div className="flex gap-6">
               {joinedCount !== undefined && <div className="text-gray-90 flex items-center gap-2">
                  <Users size={15} className="text-gray-80 shrink-0" />
                  <Text type="body2-regular">
                    <Plural value={joinedCount} one="# joined" other="# joined" />
                  </Text>
                </div>}
                {mission.type === 'FILE_UPLOAD' &&
                  mission.view.latestSubmissionStatus &&
                  mission.view.uploadedBytes > 0 && (
                    <div className="text-gray-90 flex items-center gap-2">
                      <CloudUpload size={15} className="text-gray-80 shrink-0" />
                      <Text type="body2-regular">
                        <Trans>Uploaded {uploadedSize}</Trans>
                      </Text>
                    </div>
                  )}
                {mission.type === 'WEBSITE_LOGIN' && mission.view.executedJobs > 0 && (
                  <div className="text-gray-90 flex items-center gap-2">
                    <CloudUpload size={15} className="text-gray-80 shrink-0" />
                    <Text type="body2-regular">
                      <Plural value={executedJobsCount} one="# Executed Job" other="# Executed Jobs" />
                    </Text>
                  </div>
                )}
              </div>
            )}
            <div className="relative mt-2 flex gap-2">
              <MissionCardMainActionButton mission={mission} />
              <Tooltip open={copied} onOpenChange={(open) => !open && setCopied(false)}>
                <TooltipTrigger className="w-full">
                  <Button
                    type="button"
                    className="relative flex w-full gap-2"
                    variant="bordered-gray"
                    size="small"
                    onClick={handleShare}
                  >
                    <Share /> <Trans>Share</Trans>
                  </Button>
                </TooltipTrigger>
                <TooltipContent align="end" sideOffset={4}>
                  <Trans>Copied!</Trans>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </Link>
    </li>
  )
}
